import { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ChevronDown, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';

// Professional hero images
const HERO_BG = "https://images.unsplash.com/photo-1771218830084-fdd272e149a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBzdW5zZXQlMjBtZWRpdGVycmFuZWFuJTIwdmlsbGElMjBvY2VhbiUyMHZpZXd8ZW58MHx8fHwxNzczMzE4ODI3fDA&ixlib=rb-4.1.0&q=85";

// 3D Water Drop SVG Component
const WaterDrop3D = ({ scrollYProgress, delay, startX, size = 1 }) => {
  const y = useTransform(scrollYProgress, [0, 1], [-100, 800]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360 * (delay % 2 === 0 ? 1 : -1)]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.5, size, 0.3]);
  const opacity = useTransform(scrollYProgress, [0, 0.1, 0.8, 1], [0, 1, 0.8, 0]);

  const ySpring = useSpring(y, { stiffness: 30, damping: 15 });

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: startX,
        y: ySpring,
        rotate,
        scale,
        opacity,
        transformStyle: 'preserve-3d',
      }}
    >
      <svg width="40" height="56" viewBox="0 0 40 56" className="drop-shadow-2xl">
        <defs>
          <linearGradient id={`drop3d-${delay}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
            <stop offset="30%" stopColor="rgba(56,189,248,0.8)" />
            <stop offset="70%" stopColor="rgba(14,165,233,0.9)" />
            <stop offset="100%" stopColor="rgba(2,132,199,0.7)" />
          </linearGradient>
          <filter id={`shadow-${delay}`}>
            <feDropShadow dx="2" dy="4" stdDeviation="3" floodColor="rgba(14,165,233,0.5)" />
          </filter>
        </defs>
        <path
          d="M20 0 C20 0 40 28 40 40 C40 52 30 56 20 56 C10 56 0 52 0 40 C0 28 20 0 20 0"
          fill={`url(#drop3d-${delay})`}
          filter={`url(#shadow-${delay})`}
        />
        <ellipse cx="12" cy="20" rx="6" ry="10" fill="rgba(255,255,255,0.5)" />
      </svg>
    </motion.div>
  );
};

// 3D Pool Float (Flotador) Component
const PoolFloat3D = ({ scrollYProgress, position }) => {
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [30, 0, -30]);
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [-20, 0, 20]);
  const rotateZ = useTransform(scrollYProgress, [0, 1], [0, 45]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const x = useTransform(scrollYProgress, [0, 0.5, 1], [0, 30, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.1, 0.9]);

  const springConfig = { stiffness: 50, damping: 20 };
  const rotateXSpring = useSpring(rotateX, springConfig);
  const rotateYSpring = useSpring(rotateY, springConfig);
  const ySpring = useSpring(y, springConfig);

  return (
    <motion.div
      className="absolute hidden md:block pointer-events-none"
      style={{
        ...position,
        rotateX: rotateXSpring,
        rotateY: rotateYSpring,
        rotateZ,
        y: ySpring,
        x,
        scale,
        transformStyle: 'preserve-3d',
        perspective: 1000,
      }}
    >
      <svg width="120" height="120" viewBox="0 0 120 120" className="drop-shadow-2xl">
        <defs>
          <linearGradient id="floatGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f472b6" />
            <stop offset="50%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#db2777" />
          </linearGradient>
          <linearGradient id="floatInner" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(56,189,248,0.8)" />
            <stop offset="100%" stopColor="rgba(14,165,233,0.6)" />
          </linearGradient>
        </defs>
        {/* Outer ring */}
        <circle cx="60" cy="60" r="55" fill="url(#floatGradient)" />
        <circle cx="60" cy="60" r="45" fill="url(#floatGradient)" />
        {/* Inner hole (water) */}
        <circle cx="60" cy="60" r="25" fill="url(#floatInner)" />
        {/* Highlight */}
        <ellipse cx="40" cy="40" rx="15" ry="10" fill="rgba(255,255,255,0.4)" />
        {/* Stripes */}
        <path d="M20 60 Q60 40 100 60" stroke="white" strokeWidth="8" fill="none" opacity="0.5" />
      </svg>
    </motion.div>
  );
};

// 3D Beach Ball Component
const BeachBall3D = ({ scrollYProgress, position }) => {
  const rotateX = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const rotateY = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const y = useTransform(scrollYProgress, [0, 0.5, 1], [0, -50, 100]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.6, 1, 1, 0.7]);
  const x = useTransform(scrollYProgress, [0, 0.5, 1], [0, -40, 20]);

  const springConfig = { stiffness: 40, damping: 15 };
  const rotateXSpring = useSpring(rotateX, springConfig);
  const ySpring = useSpring(y, springConfig);

  return (
    <motion.div
      className="absolute hidden lg:block pointer-events-none"
      style={{
        ...position,
        rotateX: rotateXSpring,
        rotateY,
        y: ySpring,
        x,
        scale,
        transformStyle: 'preserve-3d',
      }}
    >
      <svg width="80" height="80" viewBox="0 0 80 80" className="drop-shadow-xl">
        <defs>
          <clipPath id="ballClip">
            <circle cx="40" cy="40" r="38" />
          </clipPath>
        </defs>
        <circle cx="40" cy="40" r="38" fill="white" />
        <g clipPath="url(#ballClip)">
          <path d="M0 40 Q40 20 80 40 Q40 60 0 40" fill="#ef4444" />
          <path d="M40 0 Q20 40 40 80 Q60 40 40 0" fill="#3b82f6" />
          <path d="M0 40 Q40 60 80 40" fill="#fbbf24" />
        </g>
        <ellipse cx="25" cy="25" rx="12" ry="8" fill="rgba(255,255,255,0.5)" />
      </svg>
    </motion.div>
  );
};

// 3D Pool Ladder Component
const PoolLadder3D = ({ scrollYProgress, position }) => {
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [-15, 0, 15]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [10, 0, -10]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.85]);

  const springConfig = { stiffness: 60, damping: 20 };
  const rotateYSpring = useSpring(rotateY, springConfig);
  const ySpring = useSpring(y, springConfig);

  return (
    <motion.div
      className="absolute hidden xl:block pointer-events-none"
      style={{
        ...position,
        rotateY: rotateYSpring,
        rotateX,
        y: ySpring,
        scale,
        transformStyle: 'preserve-3d',
        perspective: 800,
      }}
    >
      <svg width="60" height="140" viewBox="0 0 60 140" className="drop-shadow-xl">
        <defs>
          <linearGradient id="metalGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="50%" stopColor="#e2e8f0" />
            <stop offset="100%" stopColor="#94a3b8" />
          </linearGradient>
        </defs>
        {/* Side rails */}
        <rect x="5" y="0" width="8" height="140" rx="4" fill="url(#metalGradient)" />
        <rect x="47" y="0" width="8" height="140" rx="4" fill="url(#metalGradient)" />
        {/* Steps */}
        <rect x="5" y="30" width="50" height="6" rx="3" fill="url(#metalGradient)" />
        <rect x="5" y="60" width="50" height="6" rx="3" fill="url(#metalGradient)" />
        <rect x="5" y="90" width="50" height="6" rx="3" fill="url(#metalGradient)" />
        {/* Curved top */}
        <path d="M9 0 Q30 -20 51 0" stroke="url(#metalGradient)" strokeWidth="8" fill="none" strokeLinecap="round" />
      </svg>
    </motion.div>
  );
};

// 3D Bubble cluster
const BubbleCluster3D = ({ scrollYProgress, position, count = 5 }) => {
  const y = useTransform(scrollYProgress, [0, 1], [100, -300]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 0.8, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.5, 1.2, 0.8]);

  const ySpring = useSpring(y, { stiffness: 30, damping: 15 });

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        ...position,
        y: ySpring,
        opacity,
        scale,
      }}
    >
      {[...Array(count)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: 10 + Math.random() * 25,
            height: 10 + Math.random() * 25,
            left: Math.random() * 50,
            top: Math.random() * 50,
            background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), rgba(56,189,248,0.4) 60%, rgba(14,165,233,0.2))',
            border: '1px solid rgba(255,255,255,0.5)',
            boxShadow: '0 0 15px rgba(56,189,248,0.3)',
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, 10 * (i % 2 === 0 ? 1 : -1), 0],
          }}
          transition={{
            duration: 3 + i * 0.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </motion.div>
  );
};

// Animated wave layers
const WaveLayers = ({ scrollYProgress }) => {
  const wave1Y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const wave2Y = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const wave3Y = useTransform(scrollYProgress, [0, 1], [0, -20]);

  return (
    <div className="absolute bottom-0 left-0 w-full h-32 sm:h-40 lg:h-48 overflow-hidden">
      {/* Wave 3 - Back */}
      <motion.div style={{ y: wave3Y }} className="absolute bottom-0 w-[200%] h-full">
        <svg viewBox="0 0 2880 120" className="w-full h-full" preserveAspectRatio="none">
          <motion.path
            d="M0,60 C480,120 960,0 1440,60 C1920,120 2400,0 2880,60 L2880,120 L0,120 Z"
            fill="rgba(255,255,255,0.2)"
            animate={{ x: [0, -1440] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />
        </svg>
      </motion.div>

      {/* Wave 2 - Middle */}
      <motion.div style={{ y: wave2Y }} className="absolute bottom-0 w-[200%] h-full">
        <svg viewBox="0 0 2880 120" className="w-full h-full" preserveAspectRatio="none">
          <motion.path
            d="M0,80 C360,20 720,100 1080,60 C1440,20 1800,100 2160,60 C2520,20 2880,100 2880,60 L2880,120 L0,120 Z"
            fill="rgba(56,189,248,0.4)"
            animate={{ x: [0, -1440] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          />
        </svg>
      </motion.div>

      {/* Wave 1 - Front */}
      <motion.div style={{ y: wave1Y }} className="absolute bottom-0 w-[200%] h-full">
        <svg viewBox="0 0 2880 120" className="w-full h-full" preserveAspectRatio="none">
          <motion.path
            d="M0,40 C720,100 1440,20 2160,80 C2520,100 2880,40 2880,40 L2880,120 L0,120 Z"
            fill="rgba(255,255,255,0.95)"
            animate={{ x: [0, -1440] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          />
        </svg>
      </motion.div>
    </div>
  );
};

export const WaterHero = () => {
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  // Parallax transforms
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const springConfig = { stiffness: 100, damping: 30 };
  const bgYSpring = useSpring(bgY, springConfig);

  // Water drop positions
  const dropPositions = [
    { x: '10%', delay: 0, size: 1.2 },
    { x: '25%', delay: 1, size: 0.8 },
    { x: '40%', delay: 2, size: 1 },
    { x: '60%', delay: 0.5, size: 1.1 },
    { x: '75%', delay: 1.5, size: 0.9 },
    { x: '90%', delay: 2.5, size: 1 },
  ];

  return (
    <section 
      ref={containerRef}
      className="relative min-h-screen lg:h-[130vh] overflow-hidden bg-slate-900"
      data-testid="hero-section"
    >
      {/* Background Image with Parallax */}
      <motion.div 
        className="absolute inset-0"
        style={{ y: bgYSpring, scale: bgScale }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${HERO_BG})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/20 to-slate-900/60" />
      </motion.div>

      {/* 3D Pool Objects */}
      <PoolFloat3D 
        scrollYProgress={scrollYProgress} 
        position={{ right: '5%', top: '15%' }} 
      />
      <BeachBall3D 
        scrollYProgress={scrollYProgress} 
        position={{ left: '8%', top: '25%' }} 
      />
      <PoolLadder3D 
        scrollYProgress={scrollYProgress} 
        position={{ right: '15%', bottom: '25%' }} 
      />

      {/* 3D Water Drops */}
      {dropPositions.map((drop, i) => (
        <WaterDrop3D
          key={i}
          scrollYProgress={scrollYProgress}
          delay={drop.delay}
          startX={drop.x}
          size={drop.size}
        />
      ))}

      {/* Bubble Clusters */}
      <BubbleCluster3D 
        scrollYProgress={scrollYProgress} 
        position={{ left: '15%', bottom: '30%' }} 
        count={6}
      />
      <BubbleCluster3D 
        scrollYProgress={scrollYProgress} 
        position={{ right: '20%', bottom: '40%' }} 
        count={4}
      />

      {/* Animated Waves */}
      <WaveLayers scrollYProgress={scrollYProgress} />

      {/* Content */}
      <motion.div 
        className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 xl:px-16 text-center lg:text-left lg:items-start max-w-7xl mx-auto py-20 lg:py-0"
        style={{ 
          y: contentY, 
          opacity: contentOpacity,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 mb-6 sm:mb-8"
        >
          <motion.div
            animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Droplets className="w-6 h-6 sm:w-8 sm:h-8 text-sky-400" />
          </motion.div>
          <span className="text-sky-300 font-semibold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-xs sm:text-sm">
            Expertos en Piscinas
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="font-manrope font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white mb-4 sm:mb-6 lg:mb-8 tracking-tight leading-[1.1]"
          data-testid="hero-title"
        >
          <motion.span 
            className="block"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            PISCINAS
          </motion.span>
          <motion.span 
            className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-cyan-300 to-teal-400"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            GENOVÉS
          </motion.span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="text-base sm:text-lg lg:text-xl text-slate-300 max-w-md sm:max-w-lg lg:max-w-xl mb-8 sm:mb-10 lg:mb-12 leading-relaxed px-4 sm:px-0"
          data-testid="hero-subtitle"
        >
          Tu oasis en la Costa Brava. Construcción, mantenimiento y 
          reparación de piscinas con la calidad que mereces.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="flex flex-col sm:flex-row gap-4 sm:gap-5 w-full sm:w-auto px-4 sm:px-0"
        >
          <Link
            to="/contacto"
            data-testid="hero-cta-primary"
            className="group relative overflow-hidden bg-gradient-to-r from-sky-500 to-cyan-500 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-bold text-base sm:text-lg transition-all duration-500 shadow-2xl shadow-sky-500/40 hover:shadow-cyan-400/60 text-center"
          >
            <span className="relative z-10">Solicitar Presupuesto</span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-sky-400"
              initial={{ x: '-100%' }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.4 }}
            />
          </Link>
          <Link
            to="/servicios"
            data-testid="hero-cta-secondary"
            className="group backdrop-blur-md bg-white/10 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 border border-white/30 hover:bg-white/20 hover:border-white/50 text-center"
          >
            Ver Servicios
          </Link>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        style={{ opacity: contentOpacity }}
        className="absolute bottom-8 sm:bottom-16 lg:bottom-24 left-1/2 -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2 sm:gap-3 text-white/70"
        >
          <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-medium">Desliza</span>
          <div className="w-6 h-10 sm:w-8 sm:h-12 rounded-full border-2 border-white/30 flex items-start justify-center p-1.5 sm:p-2">
            <motion.div
              animate={{ y: [0, 12, 0], opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 sm:w-2 sm:h-4 bg-sky-400 rounded-full"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default WaterHero;
